import { Form, FormikProvider, useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import { useSelector } from "react-redux";
import * as Yup from "yup";

import { InferenceStatus, LayerAlias, UserPolices } from "constant";
import features from "features";
import { spendTime } from "helpers/spendTime";
import { restrictToParentVerticalBounds } from "helpers/restrictToParentVerticalBounds";
import usePolicy from "hooks/usePolicy";
import { useDispatch } from "react-redux";
import { RootStateInterface } from "reducer";
import { LayersManagerStateInterface } from "scenes/LayersManager/interfaces";

import CommonButton from "components/buttons/CommonButton/CommonButton";
import PrimaryButton from "components/buttons/PrimaryButton/PrimaryButton";
import ShowHideButton from "components/buttons/ShowHideButton/ShowHideButton";
import FormField from "components/inputs/FormField/FormField";
import ModalWrapper from "components/modals/ModalWrapper/ModalWrapper";
import SortableItem from "./SortableItem";

import DownloadIcon from "assets/icons/download.svg";
import EyeCloseGrayIcon from "assets/icons/eye-close-gray.svg";
import EyeOpenGrayIcon from "assets/icons/eye-open-gray.svg";
import MarkersFlagOff from "assets/icons/markers-flag-off.svg";
import MarkersFlagOn from "assets/icons/markers-flag.svg";

import styles from "./provider-manager-modal.module.scss";

export interface ProviderManagerModalInterface {
  title?: string;
  description?: string;
  isDisableClose?: boolean;
  acceptButtonLabel?: string;
  acceptButtonAction?: () => void;
  isWithProgress?: boolean;
  id: string;
  alias: LayerAlias;
  initProviders: any;
  onHide: (updatedProviders: any) => void;
}

const ProviderManagerModal = () => {
  const dispatch = useDispatch();
  const { hasPolicy } = usePolicy();

  const {
    title,
    description,
    isDisableClose,
    alias,
    id,
    initProviders,
    onHide
  } = useSelector<RootStateInterface, ProviderManagerModalInterface>(
    (state) => state.modal.modalProps
  );

  const { providers, isCreateProviderLoading } = useSelector<
    RootStateInterface,
    LayersManagerStateInterface
  >((state) => state.layersManager);

  const sensors = useSensors(useSensor(PointerSensor));

  const providersIds = useMemo(
    () => providers.map((el) => el.id.toString()),
    [providers]
  );

  const onDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (active.id !== over?.id) {
        const newIndex = over.data.current.sortable.index;

        dispatch(
          features.layersManager.actions.reorderProviderRequest({
            params: { id, alias, providerId: active.id },
            fields: { index: newIndex }
          })
        );
      }
    },
    [alias, dispatch, id]
  );

  const initialValues = {
    link: ""
  };

  const validationSchema = Yup.object().shape({
    link: Yup.string().required("Odm link is required")
  });

  const onSubmit = useCallback(
    (e) => {
      dispatch(
        features.layersManager.actions.createProviderRequest({
          params: { id, alias },
          fields: { ...e }
        })
      );
      formik.resetForm();
    },
    [alias, dispatch, id]
  );

  const onDelete = useCallback(
    (providerId) => () => {
      dispatch(
        features.layersManager.actions.deleteProviderRequest({
          params: { id, alias, providerId }
        })
      );
    },
    [alias, dispatch, id]
  );

  const formik = useFormik({
    validateOnChange: true,
    initialValues,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    dispatch(
      features.layersManager.actions.setProviders({
        params: { alias, id },
        providers: initProviders
      })
    );
  }, [alias, dispatch, id, initProviders]);

  const inferenceDurationTime = useCallback((inference) => {
    if (!inference) return "";

    const { status, data } = inference;

    if (
      status === InferenceStatus.PROCESSING ||
      status === InferenceStatus.DONE
    )
      return spendTime({
        startTime: data.stats?.inference_time_start_at,
        endTime: data.stats?.inference_time_end_at
      });
    else if (
      status === InferenceStatus.PREPARING ||
      status === InferenceStatus.PREPARED
    ) {
      return spendTime({
        startTime: data.stats?.upload_time_start_at,
        endTime: data.stats?.upload_time_end_at
      });
    }
  }, []);

  const onUpdateVisibilityProvidersMarkers = useCallback(
    (provider) => () => {
      dispatch(
        features.layersManager.actions.setProvidersMarkersIsActive({
          providerId: provider.id,
          isActiveMarkers: !provider?.isActiveMarkers,
          alias
        })
      );
    },
    [dispatch, alias]
  );

  const isAtLeastOneVisibleProvidersMarkers = useMemo(
    () => providers.some((item) => item.isActiveMarkers),
    [providers]
  );

  const onUpdateVisibilityAllProvidersMarkers = useCallback(() => {
    dispatch(
      features.layersManager.actions.setAllProvidersMarkersIsActive({
        alias,
        isActiveMarkers: !isAtLeastOneVisibleProvidersMarkers
      })
    );
  }, [dispatch, alias, isAtLeastOneVisibleProvidersMarkers]);

  const onUpdateVisibilityProvidersTiles = useCallback(
    (provider) => () => {
      dispatch(
        features.layersManager.actions.setProvidersTilesIsActive({
          providerId: provider.id,
          isActiveTiles: !provider?.isActiveTiles,
          alias
        })
      );
    },
    [dispatch, alias]
  );

  const isAtLeastOneVisibleProvidersTiles = useMemo(
    () => providers.some((item) => item.isActiveTiles),
    [providers]
  );

  const onUpdateVisibilityAllProvidersTiles = useCallback(() => {
    dispatch(
      features.layersManager.actions.setAllProvidersTilesIsActive({
        alias,
        isActiveTiles: !isAtLeastOneVisibleProvidersTiles
      })
    );
  }, [dispatch, isAtLeastOneVisibleProvidersTiles, alias]);

  useEffect(
    () => () => {
      dispatch(features.layersManager.actions.clearProvidersState(alias));
    },
    []
  );

  const onClose = useCallback(() => {
    onHide(providers);
    dispatch(features.modal.actions.hideModal());
    dispatch(
      features.layersManager.actions.setLayerIsActive({
        params: { alias },
        fields: {
          isActive:
            isAtLeastOneVisibleProvidersTiles ||
            isAtLeastOneVisibleProvidersMarkers
        }
      })
    );
    dispatch(features.layersManager.actions.clearProvidersState(alias));
  }, [
    dispatch,
    onHide,
    providers,
    isAtLeastOneVisibleProvidersTiles,
    isAtLeastOneVisibleProvidersMarkers,
    alias
  ]);

  return (
    <ModalWrapper
      className={styles["modal-container"]}
      isDisableClose={isDisableClose}
      onHide={onClose}
    >
      <div className={styles["modal"]}>
        <div className={styles["modal__text-container"]}>
          <h2 className={styles["modal__title"]}>{title}</h2>
          <h4 className={styles["modal__description"]}>
            {description ?? "Manage files"}
          </h4>
          <div className={styles["modal__providers-table"]}>
            <div className={styles["providers-table__head"]}>
              <div className={styles["providers-table__head-controls"]}>
                <ShowHideButton
                  onClick={onUpdateVisibilityAllProvidersTiles}
                  isActive={isAtLeastOneVisibleProvidersTiles}
                  enabledIcon={EyeOpenGrayIcon}
                  disabledIcon={EyeCloseGrayIcon}
                />
                <ShowHideButton
                  onClick={onUpdateVisibilityAllProvidersMarkers}
                  isActive={isAtLeastOneVisibleProvidersMarkers}
                  enabledIcon={MarkersFlagOn}
                  disabledIcon={MarkersFlagOff}
                />
              </div>
              <div>Name</div>
              <div>ODM link</div>
              <div>Action</div>
              <div>Inference action</div>
              <div>Time</div>
              <div>Inference status</div>
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
              modifiers={[
                restrictToVerticalAxis,
                restrictToParentVerticalBounds
              ]}
            >
              <SortableContext
                items={providersIds}
                strategy={verticalListSortingStrategy}
              >
                {providers.length > 0 &&
                  providers.map((provider) => {
                    return (
                      <div key={`${provider.id}`} id={provider.id}>
                        <SortableItem
                          key={provider.id}
                          item={provider}
                          id={id}
                          alias={alias}
                          onDelete={onDelete}
                          onUpdateVisibilityProvidersMarkers={
                            onUpdateVisibilityProvidersMarkers
                          }
                          onUpdateVisibilityProvidersTiles={
                            onUpdateVisibilityProvidersTiles
                          }
                          inferenceDurationTime={inferenceDurationTime}
                        />
                      </div>
                    );
                  })}
              </SortableContext>
            </DndContext>
            {hasPolicy(UserPolices.PROJECT_PROVIDER_CREATE) && (
              <FormikProvider value={formik}>
                <Form className={styles["form"]}>
                  <FormField
                    className={styles["field"]}
                    placeholder="Add link"
                    name={"link"}
                  />
                  <CommonButton
                    type="submit"
                    icon={DownloadIcon}
                    isLoading={isCreateProviderLoading}
                    disabled={isCreateProviderLoading}
                  >
                    Add
                  </CommonButton>
                </Form>
              </FormikProvider>
            )}
          </div>
        </div>
        <div className={styles["modal__controls-wrapper"]}>
          <PrimaryButton type="button" onClick={onClose} textSize={14}>
            Done
          </PrimaryButton>
        </div>
      </div>
    </ModalWrapper>
  );
};
export default ProviderManagerModal;
